/**
 * App shell: navigation, the staleness banner (§15), the undo toast (§12.4),
 * and the sign-in screen (§4.3).
 */

import { For, Show, type JSX } from 'solid-js';
import { A, useLocation } from '@solidjs/router';
import { useInjectValue } from '@bib/solid_js_injector';

import { SESSION, TOAST } from '~/state/tokens';

import './AppShell.scss';
import { classes } from '@styx/components/AppShell.scss';

const LINKS = [
  { href: '/', label: 'Today' },
  { href: '/feed', label: 'Feed' },
  { href: '/digests', label: 'Digests' },
  { href: '/profiles', label: 'Profiles' },
  { href: '/sources', label: 'Sources' },
  { href: '/runs', label: 'Runs' },
  { href: '/settings', label: 'Settings' },
];

export function AppShell(props: { children: JSX.Element }): JSX.Element {
  const session = useInjectValue(SESSION);
  const toast = useInjectValue(TOAST);
  const location = useLocation();

  const isActive = (href: string): boolean =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <div class={classes.Host}>
      <nav class={classes.Nav} aria-label="Primary">
        <span class={classes.Brand}>Clotho</span>
        <For each={LINKS}>
          {(link) => (
            <A
              href={link.href}
              class={`${classes.Link} ${isActive(link.href) ? classes.LinkActive : ''}`}
              aria-current={isActive(link.href) ? 'page' : undefined}
            >
              {link.label}
            </A>
          )}
        </For>
        <span class={classes.Spacer} />
        <Show when={session.derivations.isAuthenticated()}>
          <span class={classes.User}>{session.derivations.displayName()}</span>
        </Show>
      </nav>

      <main class={classes.Main}>{props.children}</main>

      <Show when={toast.signals.toast()}>
        {(current) => (
          <div
            class={`${classes.Toast} ${current().tone === 'error' ? classes.ToastError : ''}`}
            role="status"
            aria-live="polite"
          >
            <span>{current().message}</span>
            <Show when={current().undo}>
              <button type="button" class={classes.ToastUndo} onClick={toast.actions.runUndo}>
                Undo
              </button>
            </Show>
          </div>
        )}
      </Show>
    </div>
  );
}

/**
 * §4.3: "On a 401 the SPA renders a single 'Sign in with Google' button
 * pointing at /auth/login." The shell itself is public and contains no user
 * data, which is what makes gating it unnecessary.
 */
export function SignInScreen(): JSX.Element {
  const session = useInjectValue(SESSION);

  return (
    <div class={classes.Host}>
      <main class={classes.Main} style={{ display: 'grid', 'place-items': 'center', 'min-height': '70vh' }}>
        <div style={{ display: 'grid', gap: '14px', 'text-align': 'center' }}>
          <h1>Clotho</h1>
          <p style={{ color: 'var(--text-dim)', 'max-width': '40ch' }}>
            Private clothing discovery. Access is limited to a fixed list of accounts.
          </p>
          <button
            type="button"
            style={{
              padding: '9px 16px',
              border: '1px solid var(--border-strong)',
              'border-radius': 'var(--radius-sm)',
              background: 'var(--bg-raised)',
            }}
            onClick={() => session.actions.signIn()}
          >
            Sign in with Google
          </button>
        </div>
      </main>
    </div>
  );
}

export function StalenessBanner(props: { lastRunAt: number | null }): JSX.Element {
  const hours = (): number =>
    props.lastRunAt === null ? Infinity : Math.round((Date.now() - props.lastRunAt) / 3_600_000);

  return (
    <div class={classes.Banner} role="status">
      <span>
        {props.lastRunAt === null
          ? 'The pipeline has never run. Nothing has been crawled yet.'
          : `The last pipeline run was ${hours()} hours ago — results below may be stale.`}
      </span>
    </div>
  );
}
